import { useRef, Suspense } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Environment } from '@react-three/drei';
import { motion, AnimatePresence } from 'framer-motion';
import { useAvatarAnimation } from '@/hooks/useAvatarAnimation';
import * as THREE from 'three';
import './Avatar.css';

interface AvatarProps {
  isSpeaking: boolean;
}

// Cute Robot Component with Spline-style design
function CuteRobot({ isSpeaking }: { isSpeaking: boolean }) {
  const robotRef = useRef<THREE.Group>(null);
  const headRef = useRef<THREE.Group>(null);
  const faceScreenRef = useRef<THREE.Mesh>(null);
  const leftEyeRef = useRef<THREE.Mesh>(null);
  const rightEyeRef = useRef<THREE.Mesh>(null);
  const mouthRef = useRef<THREE.Mesh>(null);
  const leftArmRef = useRef<THREE.Group>(null);
  const rightArmRef = useRef<THREE.Group>(null);
  const bodyRef = useRef<THREE.Group>(null);
  const leftLegRef = useRef<THREE.Group>(null);
  const rightLegRef = useRef<THREE.Group>(null);

  const blinkTimer = useRef(0);
  const speakTimer = useRef(0);

  useFrame((state, delta) => {
    if (!robotRef.current) return;

    // Mechanical head rotation - more robotic
    if (headRef.current && !isSpeaking) {
      const time = state.clock.elapsedTime;
      // Smooth scanning motion
      headRef.current.rotation.y = Math.sin(time * 0.4) * 0.2;
      headRef.current.rotation.x = Math.sin(time * 0.25) * 0.08;
    }

    // Blinking animation
    blinkTimer.current += delta;
    if (blinkTimer.current > 3) {
      blinkTimer.current = 0;
    }
    const blinkProgress =
      blinkTimer.current < 0.12
        ? blinkTimer.current / 0.12
        : blinkTimer.current < 0.2
          ? 1 - (blinkTimer.current - 0.12) / 0.08
          : 0;

    if (leftEyeRef.current && rightEyeRef.current) {
      const blinkScale = 1 - blinkProgress;
      leftEyeRef.current.scale.y = blinkScale;
      rightEyeRef.current.scale.y = blinkScale;
    }

    // Speaking animation - enhanced with facial expressions
    if (isSpeaking && mouthRef.current) {
      speakTimer.current += delta * 12;
      const mouthOpen = Math.sin(speakTimer.current) * 0.5 + 0.5;
      const mouthVariation = Math.sin(speakTimer.current * 1.8) * 0.3;

      // Smooth mouth scaling with more variation
      const targetScaleY = 0.5 + mouthOpen * 0.8 + mouthVariation * 0.2;
      const targetScaleX = 1.2 - mouthOpen * 0.3;

      mouthRef.current.scale.y = THREE.MathUtils.lerp(mouthRef.current.scale.y, targetScaleY, 0.3);
      mouthRef.current.scale.x = THREE.MathUtils.lerp(mouthRef.current.scale.x, targetScaleX, 0.3);

      // Enhanced mouth glow when speaking
      if (faceScreenRef.current) {
        const material = faceScreenRef.current.material as THREE.MeshStandardMaterial;
        material.emissiveIntensity = 0.5 + mouthOpen * 0.7;
      }

      // Robotic head movement while speaking
      if (headRef.current) {
        const time = state.clock.elapsedTime;
        // Mechanical nod
        headRef.current.position.y = Math.sin(time * 3) * 0.04;
        headRef.current.rotation.x = Math.sin(time * 2.5) * 0.08;
        // Slight head tilt
        headRef.current.rotation.z = Math.sin(time * 1.8) * 0.04;
        // Scanning rotation
        headRef.current.rotation.y = Math.sin(time * 0.7) * 0.12;
      }

      // Eyes squint slightly when speaking (happy expression)
      if (leftEyeRef.current && rightEyeRef.current && blinkProgress === 0) {
        const squint = Math.sin(state.clock.elapsedTime * 3) * 0.1 + 0.9;
        leftEyeRef.current.scale.y = squint;
        rightEyeRef.current.scale.y = squint;
      }

      // Robotic hand gestures while speaking - more mechanical
      if (leftArmRef.current && rightArmRef.current) {
        const gestureTime = state.clock.elapsedTime;
        // Left hand - robotic stepped motion
        const leftGesture = Math.floor(Math.sin(gestureTime * 1.5) * 3) / 3;
        leftArmRef.current.rotation.z = -0.25 + leftGesture * 0.35;
        leftArmRef.current.rotation.x = Math.sin(gestureTime * 0.8) * 0.15;
        leftArmRef.current.rotation.y = Math.sin(gestureTime * 0.6) * 0.1;

        // Right hand - alternating robotic motion
        const rightGesture = Math.floor(Math.sin(gestureTime * 1.5 + Math.PI) * 3) / 3;
        rightArmRef.current.rotation.z = 0.25 + rightGesture * 0.35;
        rightArmRef.current.rotation.x = Math.sin(gestureTime * 0.8 + Math.PI) * 0.15;
        rightArmRef.current.rotation.y = Math.sin(gestureTime * 0.6 + Math.PI) * 0.1;
      }

      // Body slight movement while speaking
      if (bodyRef.current) {
        bodyRef.current.rotation.y = Math.sin(state.clock.elapsedTime * 0.8) * 0.05;
      }

      // Subtle leg weight shift while speaking
      if (leftLegRef.current && rightLegRef.current) {
        const shiftTime = state.clock.elapsedTime * 0.6;
        leftLegRef.current.rotation.x = Math.sin(shiftTime) * 0.05;
        rightLegRef.current.rotation.x = Math.sin(shiftTime + Math.PI) * 0.05;
      }
    } else if (mouthRef.current) {
      mouthRef.current.scale.y = THREE.MathUtils.lerp(mouthRef.current.scale.y, 0.5, 0.2);
      mouthRef.current.scale.x = THREE.MathUtils.lerp(mouthRef.current.scale.x, 1.2, 0.2);

      if (faceScreenRef.current) {
        const material = faceScreenRef.current.material as THREE.MeshStandardMaterial;
        material.emissiveIntensity = THREE.MathUtils.lerp(material.emissiveIntensity, 0.3, 0.1);
      }

      // Reset head position when not speaking
      if (headRef.current) {
        headRef.current.position.y = THREE.MathUtils.lerp(headRef.current.position.y, 0, 0.1);
        headRef.current.rotation.z = THREE.MathUtils.lerp(headRef.current.rotation.z, 0, 0.1);
      }

      // Reset hand positions
      if (leftArmRef.current && rightArmRef.current) {
        leftArmRef.current.rotation.z = THREE.MathUtils.lerp(
          leftArmRef.current.rotation.z,
          -0.25,
          0.1
        );
        leftArmRef.current.rotation.x = THREE.MathUtils.lerp(leftArmRef.current.rotation.x, 0, 0.1);
        rightArmRef.current.rotation.z = THREE.MathUtils.lerp(
          rightArmRef.current.rotation.z,
          0.25,
          0.1
        );
        rightArmRef.current.rotation.x = THREE.MathUtils.lerp(
          rightArmRef.current.rotation.x,
          0,
          0.1
        );
      }

      // Reset body rotation
      if (bodyRef.current) {
        bodyRef.current.rotation.y = THREE.MathUtils.lerp(bodyRef.current.rotation.y, 0, 0.1);
      }

      // Reset leg positions
      if (leftLegRef.current && rightLegRef.current) {
        leftLegRef.current.rotation.x = THREE.MathUtils.lerp(leftLegRef.current.rotation.x, 0, 0.1);
        rightLegRef.current.rotation.x = THREE.MathUtils.lerp(
          rightLegRef.current.rotation.x,
          0,
          0.1
        );
      }
    }
  });

  return (
    <group ref={robotRef} scale={0.75} position={[0, 0, 0]}>
      {/* Robot Head */}
      <group ref={headRef} position={[0, 1.2, 0]}>
        {/* Head main structure - box style */}
        <mesh>
          <boxGeometry args={[1.5, 1.3, 1.2]} />
          <meshStandardMaterial color="#8b9dc3" roughness={0.1} metalness={0.95} />
        </mesh>

        {/* Head panel lines - mechanical details */}
        <mesh position={[0, 0.3, 0.61]}>
          <boxGeometry args={[1.22, 0.02, 0.01]} />
          <meshStandardMaterial color="#2d3748" roughness={0.1} metalness={0.98} />
        </mesh>
        <mesh position={[0, -0.3, 0.61]}>
          <boxGeometry args={[1.22, 0.02, 0.01]} />
          <meshStandardMaterial color="#2d3748" roughness={0.1} metalness={0.98} />
        </mesh>

        {/* Forehead sensor panel */}
        <mesh position={[0, 0.45, 0.61]}>
          <boxGeometry args={[0.35, 0.08, 0.02]} />
          <meshStandardMaterial color="#1e293b" roughness={0.3} metalness={0.7} />
        </mesh>
        {/* Sensor lights */}
        <mesh position={[-0.1, 0.45, 0.62]}>
          <sphereGeometry args={[0.018, 16, 16]} />
          <meshStandardMaterial
            color="#ef4444"
            emissive="#ef4444"
            emissiveIntensity={2}
            roughness={0.1}
          />
        </mesh>
        <mesh position={[0, 0.45, 0.62]}>
          <sphereGeometry args={[0.018, 16, 16]} />
          <meshStandardMaterial
            color="#eab308"
            emissive="#eab308"
            emissiveIntensity={2}
            roughness={0.1}
          />
        </mesh>
        <mesh position={[0.1, 0.45, 0.62]}>
          <sphereGeometry args={[0.018, 16, 16]} />
          <meshStandardMaterial
            color="#10b981"
            emissive="#10b981"
            emissiveIntensity={2}
            roughness={0.1}
          />
        </mesh>

        {/* Head side panels - decorative */}
        <mesh position={[-0.76, 0, 0]}>
          <boxGeometry args={[0.02, 1.1, 1]} />
          <meshStandardMaterial color="#1e293b" roughness={0.1} metalness={0.98} />
        </mesh>
        <mesh position={[0.76, 0, 0]}>
          <boxGeometry args={[0.02, 1.1, 1]} />
          <meshStandardMaterial color="#1e293b" roughness={0.1} metalness={0.98} />
        </mesh>

        {/* Screen frame border - outermost */}
        <mesh position={[0, 0, 0.61]}>
          <boxGeometry args={[1.28, 1.08, 0.02]} />
          <meshStandardMaterial color="#64748b" roughness={0.1} metalness={0.95} />
        </mesh>

        {/* Face Screen - friendly soft screen */}
        <mesh ref={faceScreenRef} position={[0, 0, 0.62]}>
          <boxGeometry args={[1.2, 1, 0.03]} />
          <meshStandardMaterial
            color="#1e3a8a"
            roughness={0.05}
            metalness={0.9}
            emissive="#3b82f6"
            emissiveIntensity={0.5}
          />
        </mesh>

        {/* Screen inner border glow */}
        <mesh position={[0, 0, 0.64]}>
          <boxGeometry args={[1.15, 0.95, 0.01]} />
          <meshStandardMaterial
            color="#3b82f6"
            emissive="#3b82f6"
            emissiveIntensity={0.3}
            transparent
            opacity={0.3}
          />
        </mesh>

        {/* Left Eye - cute happy round eye */}
        <mesh ref={leftEyeRef} position={[-0.3, 0.2, 0.65]}>
          <sphereGeometry args={[0.1, 32, 32]} />
          <meshStandardMaterial
            color="#ffffff"
            emissive="#60a5fa"
            emissiveIntensity={0.5}
            roughness={0.05}
            metalness={0.1}
          />
        </mesh>
        {/* Left pupil */}
        <mesh position={[-0.3, 0.2, 0.75]}>
          <sphereGeometry args={[0.05, 16, 16]} />
          <meshStandardMaterial color="#1e293b" roughness={0.1} />
        </mesh>
        {/* Left eye shine */}
        <mesh position={[-0.28, 0.23, 0.78]}>
          <sphereGeometry args={[0.025, 16, 16]} />
          <meshStandardMaterial color="#ffffff" emissive="#ffffff" emissiveIntensity={2} />
        </mesh>

        {/* Right Eye - cute happy round eye */}
        <mesh ref={rightEyeRef} position={[0.3, 0.2, 0.65]}>
          <sphereGeometry args={[0.1, 32, 32]} />
          <meshStandardMaterial
            color="#ffffff"
            emissive="#60a5fa"
            emissiveIntensity={0.5}
            roughness={0.05}
            metalness={0.1}
          />
        </mesh>
        {/* Right pupil */}
        <mesh position={[0.3, 0.2, 0.75]}>
          <sphereGeometry args={[0.05, 16, 16]} />
          <meshStandardMaterial color="#1e293b" roughness={0.1} />
        </mesh>
        {/* Right eye shine */}
        <mesh position={[0.32, 0.23, 0.78]}>
          <sphereGeometry args={[0.025, 16, 16]} />
          <meshStandardMaterial color="#ffffff" emissive="#ffffff" emissiveIntensity={2} />
        </mesh>

        {/* Mouth - happy smile */}
        <mesh ref={mouthRef} position={[0, -0.25, 0.65]} rotation={[0, 0, Math.PI]}>
          <torusGeometry args={[0.18, 0.035, 16, 32, Math.PI]} />
          <meshStandardMaterial
            color="#3b82f6"
            emissive="#3b82f6"
            emissiveIntensity={1.5}
            roughness={0.05}
            metalness={0.8}
          />
        </mesh>

        {/* Metallic sensor spheres */}
        <mesh position={[-0.5, -0.05, 0.64]}>
          <sphereGeometry args={[0.08, 16, 16]} />
          <meshStandardMaterial
            color="#64748b"
            emissive="#3b82f6"
            emissiveIntensity={0.3}
            roughness={0.1}
            metalness={0.95}
          />
        </mesh>
        <mesh position={[0.5, -0.05, 0.64]}>
          <sphereGeometry args={[0.08, 16, 16]} />
          <meshStandardMaterial
            color="#64748b"
            emissive="#3b82f6"
            emissiveIntensity={0.3}
            roughness={0.1}
            metalness={0.95}
          />
        </mesh>

        {/* Antenna base */}
        <mesh position={[0, 0.75, 0]}>
          <cylinderGeometry args={[0.06, 0.08, 0.15, 16]} />
          <meshStandardMaterial color="#334155" roughness={0.1} metalness={0.98} />
        </mesh>

        {/* Antenna rod */}
        <mesh position={[0, 0.95, 0]}>
          <cylinderGeometry args={[0.025, 0.025, 0.3, 16]} />
          <meshStandardMaterial color="#1e293b" roughness={0.08} metalness={0.98} />
        </mesh>

        {/* Antenna tip - red LED */}
        <mesh position={[0, 1.15, 0]} scale={[1, 1, 0.7]}>
          <sphereGeometry args={[0.09, 32, 32]} />
          <meshStandardMaterial
            color="#ef4444"
            emissive="#ef4444"
            emissiveIntensity={3}
            roughness={0.05}
            metalness={0.5}
          />
        </mesh>
        <mesh position={[-0.06, 1.18, 0]} scale={[1, 1, 0.7]}>
          <sphereGeometry args={[0.05, 32, 32]} />
          <meshStandardMaterial
            color="#ef4444"
            emissive="#ef4444"
            emissiveIntensity={3}
            roughness={0.05}
            metalness={0.5}
          />
        </mesh>
        <mesh position={[0.06, 1.18, 0]} scale={[1, 1, 0.7]}>
          <sphereGeometry args={[0.05, 32, 32]} />
          <meshStandardMaterial
            color="#f472b6"
            emissive="#f472b6"
            emissiveIntensity={2.5}
            roughness={0.05}
          />
        </mesh>

        {/* Ears/Side panels - more detailed */}
        <group position={[-0.85, 0, 0]}>
          <mesh rotation={[0, 0, Math.PI / 2]}>
            <cylinderGeometry args={[0.14, 0.16, 0.25, 32]} />
            <meshStandardMaterial color="#b8b9d0" roughness={0.25} metalness={0.75} />
          </mesh>
          {/* Ear detail circles */}
          <mesh position={[0, 0, 0.13]}>
            <cylinderGeometry args={[0.08, 0.08, 0.02, 16]} />
            <meshStandardMaterial color="#7c7d9a" roughness={0.3} metalness={0.7} />
          </mesh>
        </group>

        <group position={[0.85, 0, 0]}>
          <mesh rotation={[0, 0, Math.PI / 2]}>
            <cylinderGeometry args={[0.14, 0.16, 0.25, 32]} />
            <meshStandardMaterial color="#b8b9d0" roughness={0.25} metalness={0.75} />
          </mesh>
          {/* Ear detail circles */}
          <mesh position={[0, 0, -0.13]}>
            <cylinderGeometry args={[0.08, 0.08, 0.02, 16]} />
            <meshStandardMaterial color="#7c7d9a" roughness={0.3} metalness={0.7} />
          </mesh>
        </group>
      </group>

      {/* Neck connector - more detailed */}
      <mesh position={[0, 0.7, 0]}>
        <cylinderGeometry args={[0.25, 0.3, 0.2, 32]} />
        <meshStandardMaterial color="#475569" roughness={0.1} metalness={0.98} />
      </mesh>
      {/* Neck joint details */}
      <mesh position={[0, 0.8, 0]}>
        <torusGeometry args={[0.26, 0.02, 16, 32]} />
        <meshStandardMaterial color="#1e293b" roughness={0.05} metalness={0.98} />
      </mesh>
      <mesh position={[0, 0.6, 0]}>
        <torusGeometry args={[0.31, 0.02, 16, 32]} />
        <meshStandardMaterial color="#1e293b" roughness={0.05} metalness={0.98} />
      </mesh>

      {/* Body - improved structure */}
      <group ref={bodyRef}>
        {/* Main torso - upper section */}
        <mesh position={[0, 0.3, 0]}>
          <boxGeometry args={[1.1, 0.5, 0.7]} />
          <meshStandardMaterial color="#94a3b8" roughness={0.1} metalness={0.96} />
        </mesh>
        {/* Chest plate rounded top */}
        <mesh position={[0, 0.55, 0]}>
          <capsuleGeometry args={[0.55, 0, 16, 32]} rotation={[0, 0, Math.PI / 2]} />
          <meshStandardMaterial color="#94a3b8" roughness={0.1} metalness={0.96} />
        </mesh>

        {/* Lower torso */}
        <mesh position={[0, -0.05, 0]}>
          <boxGeometry args={[0.95, 0.4, 0.65]} />
          <meshStandardMaterial color="#64748b" roughness={0.12} metalness={0.95} />
        </mesh>

        {/* Chest arc reactor - power core */}
        <mesh position={[0, 0.35, 0.36]} rotation={[Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[0.12, 0.12, 0.04, 32]} />
          <meshStandardMaterial
            color="#2563eb"
            emissive="#3b82f6"
            emissiveIntensity={2.5}
            roughness={0.1}
            metalness={0.9}
          />
        </mesh>
        {/* Arc reactor inner glow */}
        <mesh position={[0, 0.35, 0.38]} rotation={[Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[0.08, 0.08, 0.02, 32]} />
          <meshStandardMaterial
            color="#60a5fa"
            emissive="#60a5fa"
            emissiveIntensity={4}
            roughness={0.05}
          />
        </mesh>
        {/* Arc reactor ring */}
        <mesh position={[0, 0.35, 0.37]} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.12, 0.015, 16, 32]} />
          <meshStandardMaterial color="#1e293b" roughness={0.1} metalness={0.98} />
        </mesh>

        {/* Shoulder panels */}
        <mesh position={[-0.56, 0.45, 0]}>
          <boxGeometry args={[0.08, 0.25, 0.6]} />
          <meshStandardMaterial color="#334155" roughness={0.1} metalness={0.98} />
        </mesh>
        <mesh position={[0.56, 0.45, 0]}>
          <boxGeometry args={[0.08, 0.25, 0.6]} />
          <meshStandardMaterial color="#334155" roughness={0.1} metalness={0.98} />
        </mesh>

        {/* Body panel lines */}
        <mesh position={[-0.25, 0.3, 0.36]}>
          <boxGeometry args={[0.02, 0.4, 0.02]} />
          <meshStandardMaterial color="#1e293b" roughness={0.1} metalness={0.98} />
        </mesh>
        <mesh position={[0.25, 0.3, 0.36]}>
          <boxGeometry args={[0.02, 0.4, 0.02]} />
          <meshStandardMaterial color="#1e293b" roughness={0.1} metalness={0.98} />
        </mesh>

        {/* Waist joint */}
        <mesh position={[0, -0.25, 0]}>
          <cylinderGeometry args={[0.4, 0.45, 0.08, 32]} />
          <meshStandardMaterial color="#b8b9d0" roughness={0.2} metalness={0.85} />
        </mesh>

        {/* Belt detail */}
        <mesh position={[0, -0.28, 0]}>
          <torusGeometry args={[0.46, 0.03, 16, 32]} />
          <meshStandardMaterial color="#60a5fa" roughness={0.15} metalness={0.9} />
        </mesh>

        {/* Status indicator lights on chest */}
        <mesh position={[-0.15, 0.2, 0.36]}>
          <sphereGeometry args={[0.025, 16, 16]} />
          <meshStandardMaterial
            color="#10b981"
            emissive="#10b981"
            emissiveIntensity={2}
            roughness={0.1}
          />
        </mesh>
        <mesh position={[0.15, 0.2, 0.36]}>
          <sphereGeometry args={[0.025, 16, 16]} />
          <meshStandardMaterial
            color="#10b981"
            emissive="#10b981"
            emissiveIntensity={2}
            roughness={0.1}
          />
        </mesh>

        {/* Mechanical vent panels on chest sides */}
        <mesh position={[-0.45, 0.3, 0.32]}>
          <boxGeometry args={[0.15, 0.35, 0.08]} />
          <meshStandardMaterial color="#1a1a2e" roughness={0.4} metalness={0.6} />
        </mesh>
        <mesh position={[0.45, 0.3, 0.32]}>
          <boxGeometry args={[0.15, 0.35, 0.08]} />
          <meshStandardMaterial color="#1a1a2e" roughness={0.4} metalness={0.6} />
        </mesh>

        {/* Vent grill lines */}
        {[-0.45, 0.45].map((xPos) => (
          <group key={xPos}>
            {[0.38, 0.33, 0.28, 0.23, 0.18].map((yPos, i) => (
              <mesh key={i} position={[xPos, yPos, 0.36]}>
                <boxGeometry args={[0.12, 0.015, 0.01]} />
                <meshStandardMaterial color="#60a5fa" roughness={0.2} metalness={0.85} />
              </mesh>
            ))}
          </group>
        ))}

        {/* Lower body mechanical details */}
        <mesh position={[0, -0.15, 0.33]}>
          <boxGeometry args={[0.7, 0.15, 0.04]} />
          <meshStandardMaterial color="#7c7d9a" roughness={0.18} metalness={0.85} />
        </mesh>
        {/* Circuit pattern on lower torso */}
        <mesh position={[-0.2, -0.15, 0.34]}>
          <boxGeometry args={[0.08, 0.02, 0.01]} />
          <meshStandardMaterial
            color="#3b82f6"
            emissive="#3b82f6"
            emissiveIntensity={0.8}
            roughness={0.1}
          />
        </mesh>
        <mesh position={[0.2, -0.15, 0.34]}>
          <boxGeometry args={[0.08, 0.02, 0.01]} />
          <meshStandardMaterial
            color="#3b82f6"
            emissive="#3b82f6"
            emissiveIntensity={0.8}
            roughness={0.1}
          />
        </mesh>
      </group>

      {/* Arms - left with enhanced details */}
      <group ref={leftArmRef} position={[-0.62, 0.35, 0]} rotation={[0, 0, -0.25]}>
        {/* Shoulder joint - ball socket */}
        <mesh>
          <sphereGeometry args={[0.16, 32, 32]} />
          <meshStandardMaterial color="#b8b9d0" roughness={0.15} metalness={0.85} />
        </mesh>
        {/* Shoulder housing */}
        <mesh position={[0, 0.05, 0]}>
          <cylinderGeometry args={[0.14, 0.12, 0.1, 32]} />
          <meshStandardMaterial color="#7c7d9a" roughness={0.2} metalness={0.8} />
        </mesh>

        {/* Upper arm - segmented */}
        <mesh position={[0, -0.15, 0]}>
          <cylinderGeometry args={[0.11, 0.11, 0.2, 32]} />
          <meshStandardMaterial color="#e8e9f3" roughness={0.15} metalness={0.85} />
        </mesh>
        <mesh position={[0, -0.05, 0]}>
          <torusGeometry args={[0.11, 0.02, 16, 32]} />
          <meshStandardMaterial color="#60a5fa" roughness={0.15} metalness={0.9} />
        </mesh>
        <mesh position={[0, -0.25, 0]}>
          <cylinderGeometry args={[0.11, 0.1, 0.1, 32]} />
          <meshStandardMaterial color="#e8e9f3" roughness={0.15} metalness={0.85} />
        </mesh>

        {/* Elbow joint - detailed */}
        <mesh position={[0, -0.35, 0]}>
          <sphereGeometry args={[0.13, 32, 32]} />
          <meshStandardMaterial color="#b8b9d0" roughness={0.15} metalness={0.85} />
        </mesh>
        <mesh position={[0, -0.35, 0]}>
          <torusGeometry args={[0.1, 0.015, 16, 32]} />
          <meshStandardMaterial color="#7c7d9a" roughness={0.2} metalness={0.8} />
        </mesh>

        {/* Forearm - segmented */}
        <mesh position={[0, -0.48, 0]}>
          <cylinderGeometry args={[0.1, 0.095, 0.18, 32]} />
          <meshStandardMaterial color="#e8e9f3" roughness={0.15} metalness={0.85} />
        </mesh>
        <mesh position={[0, -0.42, 0]}>
          <torusGeometry args={[0.1, 0.015, 16, 32]} />
          <meshStandardMaterial color="#60a5fa" roughness={0.15} metalness={0.9} />
        </mesh>

        {/* Wrist joint */}
        <mesh position={[0, -0.6, 0]}>
          <cylinderGeometry args={[0.085, 0.09, 0.08, 32]} />
          <meshStandardMaterial color="#b8b9d0" roughness={0.15} metalness={0.85} />
        </mesh>

        {/* Hand - more detailed */}
        <mesh position={[0, -0.7, 0]}>
          <boxGeometry args={[0.18, 0.12, 0.22]} />
          <meshStandardMaterial color="#b8b9d0" roughness={0.15} metalness={0.85} />
        </mesh>
        {/* Fingers */}
        <mesh position={[0, -0.78, 0.08]}>
          <boxGeometry args={[0.14, 0.04, 0.08]} />
          <meshStandardMaterial color="#9b9cb8" roughness={0.18} metalness={0.82} />
        </mesh>
        {/* Palm detail */}
        <mesh position={[0, -0.7, 0.12]} rotation={[Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[0.035, 0.035, 0.02, 16]} />
          <meshStandardMaterial
            color="#60a5fa"
            emissive="#60a5fa"
            emissiveIntensity={1.2}
            roughness={0.1}
          />
        </mesh>
      </group>

      {/* Arms - right with enhanced details */}
      <group ref={rightArmRef} position={[0.62, 0.35, 0]} rotation={[0, 0, 0.25]}>
        {/* Shoulder joint - ball socket */}
        <mesh>
          <sphereGeometry args={[0.16, 32, 32]} />
          <meshStandardMaterial color="#b8b9d0" roughness={0.15} metalness={0.85} />
        </mesh>
        {/* Shoulder housing */}
        <mesh position={[0, 0.05, 0]}>
          <cylinderGeometry args={[0.14, 0.12, 0.1, 32]} />
          <meshStandardMaterial color="#7c7d9a" roughness={0.2} metalness={0.8} />
        </mesh>

        {/* Upper arm - segmented */}
        <mesh position={[0, -0.15, 0]}>
          <cylinderGeometry args={[0.11, 0.11, 0.2, 32]} />
          <meshStandardMaterial color="#e8e9f3" roughness={0.15} metalness={0.85} />
        </mesh>
        <mesh position={[0, -0.05, 0]}>
          <torusGeometry args={[0.11, 0.02, 16, 32]} />
          <meshStandardMaterial color="#60a5fa" roughness={0.15} metalness={0.9} />
        </mesh>
        <mesh position={[0, -0.25, 0]}>
          <cylinderGeometry args={[0.11, 0.1, 0.1, 32]} />
          <meshStandardMaterial color="#e8e9f3" roughness={0.15} metalness={0.85} />
        </mesh>

        {/* Elbow joint - detailed */}
        <mesh position={[0, -0.35, 0]}>
          <sphereGeometry args={[0.13, 32, 32]} />
          <meshStandardMaterial color="#b8b9d0" roughness={0.15} metalness={0.85} />
        </mesh>
        <mesh position={[0, -0.35, 0]}>
          <torusGeometry args={[0.1, 0.015, 16, 32]} />
          <meshStandardMaterial color="#7c7d9a" roughness={0.2} metalness={0.8} />
        </mesh>

        {/* Forearm - segmented */}
        <mesh position={[0, -0.48, 0]}>
          <cylinderGeometry args={[0.1, 0.095, 0.18, 32]} />
          <meshStandardMaterial color="#e8e9f3" roughness={0.15} metalness={0.85} />
        </mesh>
        <mesh position={[0, -0.42, 0]}>
          <torusGeometry args={[0.1, 0.015, 16, 32]} />
          <meshStandardMaterial color="#60a5fa" roughness={0.15} metalness={0.9} />
        </mesh>

        {/* Wrist joint */}
        <mesh position={[0, -0.6, 0]}>
          <cylinderGeometry args={[0.085, 0.09, 0.08, 32]} />
          <meshStandardMaterial color="#b8b9d0" roughness={0.15} metalness={0.85} />
        </mesh>

        {/* Hand - more detailed */}
        <mesh position={[0, -0.7, 0]}>
          <boxGeometry args={[0.18, 0.12, 0.22]} />
          <meshStandardMaterial color="#b8b9d0" roughness={0.15} metalness={0.85} />
        </mesh>
        {/* Fingers */}
        <mesh position={[0, -0.78, 0.08]}>
          <boxGeometry args={[0.14, 0.04, 0.08]} />
          <meshStandardMaterial color="#9b9cb8" roughness={0.18} metalness={0.82} />
        </mesh>
        {/* Palm detail */}
        <mesh position={[0, -0.7, 0.12]} rotation={[Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[0.035, 0.035, 0.02, 16]} />
          <meshStandardMaterial
            color="#60a5fa"
            emissive="#60a5fa"
            emissiveIntensity={1.2}
            roughness={0.1}
          />
        </mesh>
      </group>

      {/* Pelvis/Hip structure */}
      <mesh position={[0, -0.35, 0]}>
        <boxGeometry args={[0.8, 0.25, 0.5]} />
        <meshStandardMaterial color="#475569" roughness={0.1} metalness={0.97} />
      </mesh>
      {/* Hip joint covers */}
      <mesh position={[-0.25, -0.42, 0]}>
        <sphereGeometry args={[0.18, 32, 32]} />
        <meshStandardMaterial color="#334155" roughness={0.1} metalness={0.98} />
      </mesh>
      <mesh position={[0.25, -0.42, 0]}>
        <sphereGeometry args={[0.18, 32, 32]} />
        <meshStandardMaterial color="#334155" roughness={0.1} metalness={0.98} />
      </mesh>
      {/* Pelvis detail belt */}
      <mesh position={[0, -0.35, 0.26]}>
        <boxGeometry args={[0.75, 0.08, 0.04]} />
        <meshStandardMaterial color="#1e293b" roughness={0.08} metalness={0.98} />
      </mesh>

      {/* Left Leg */}
      <group ref={leftLegRef} position={[-0.25, -0.5, 0]}>
        {/* Hip joint */}
        <mesh>
          <sphereGeometry args={[0.15, 32, 32]} />
          <meshStandardMaterial color="#b8b9d0" roughness={0.15} metalness={0.85} />
        </mesh>

        {/* Upper leg (thigh) */}
        <mesh position={[0, -0.35, 0]}>
          <capsuleGeometry args={[0.14, 0.5, 32, 32]} />
          <meshStandardMaterial color="#ffffff" roughness={0.1} metalness={0.95} />
        </mesh>
        {/* Thigh armor panel */}
        <mesh position={[0, -0.35, 0.15]}>
          <boxGeometry args={[0.22, 0.45, 0.08]} />
          <meshStandardMaterial color="#e8e9f3" roughness={0.12} metalness={0.92} />
        </mesh>
        {/* Thigh detail ring */}
        <mesh position={[0, -0.15, 0]}>
          <torusGeometry args={[0.145, 0.02, 16, 32]} />
          <meshStandardMaterial color="#60a5fa" roughness={0.15} metalness={0.9} />
        </mesh>

        {/* Knee joint */}
        <mesh position={[0, -0.65, 0]}>
          <sphereGeometry args={[0.16, 32, 32]} />
          <meshStandardMaterial color="#b8b9d0" roughness={0.15} metalness={0.85} />
        </mesh>
        {/* Knee cap detail */}
        <mesh position={[0, -0.65, 0.12]}>
          <capsuleGeometry args={[0.12, 0.08, 16, 32]} rotation={[0, 0, Math.PI / 2]} />
          <meshStandardMaterial color="#7c7d9a" roughness={0.2} metalness={0.8} />
        </mesh>

        {/* Lower leg (shin) */}
        <mesh position={[0, -0.95, 0]}>
          <capsuleGeometry args={[0.13, 0.45, 32, 32]} />
          <meshStandardMaterial color="#e8e9f3" roughness={0.12} metalness={0.92} />
        </mesh>
        {/* Shin armor panel */}
        <mesh position={[0, -0.95, 0.14]}>
          <boxGeometry args={[0.2, 0.4, 0.06]} />
          <meshStandardMaterial color="#ffffff" roughness={0.1} metalness={0.95} />
        </mesh>
        {/* Shin detail stripes */}
        <mesh position={[0, -0.85, 0.17]}>
          <boxGeometry args={[0.18, 0.03, 0.02]} />
          <meshStandardMaterial color="#60a5fa" roughness={0.15} metalness={0.9} />
        </mesh>
        <mesh position={[0, -1.05, 0.17]}>
          <boxGeometry args={[0.18, 0.03, 0.02]} />
          <meshStandardMaterial color="#60a5fa" roughness={0.15} metalness={0.9} />
        </mesh>

        {/* Ankle joint */}
        <mesh position={[0, -1.2, 0]}>
          <cylinderGeometry args={[0.11, 0.13, 0.1, 32]} />
          <meshStandardMaterial color="#b8b9d0" roughness={0.15} metalness={0.85} />
        </mesh>

        {/* Foot */}
        <mesh position={[0, -1.32, 0.08]}>
          <boxGeometry args={[0.22, 0.12, 0.35]} />
          <meshStandardMaterial color="#7c7d9a" roughness={0.18} metalness={0.88} />
        </mesh>
        {/* Foot top plate */}
        <mesh position={[0, -1.26, 0.08]}>
          <boxGeometry args={[0.24, 0.02, 0.37]} />
          <meshStandardMaterial color="#b8b9d0" roughness={0.15} metalness={0.9} />
        </mesh>
        {/* Toe section */}
        <mesh position={[0, -1.32, 0.22]}>
          <boxGeometry args={[0.22, 0.1, 0.12]} />
          <meshStandardMaterial color="#60a5fa" roughness={0.15} metalness={0.9} />
        </mesh>
        {/* Heel detail */}
        <mesh position={[0, -1.36, -0.06]}>
          <boxGeometry args={[0.2, 0.08, 0.1]} />
          <meshStandardMaterial color="#60a5fa" roughness={0.15} metalness={0.9} />
        </mesh>
        {/* Foot glow accent */}
        <mesh position={[0, -1.31, 0.25]}>
          <boxGeometry args={[0.15, 0.04, 0.02]} />
          <meshStandardMaterial
            color="#3b82f6"
            emissive="#3b82f6"
            emissiveIntensity={1.5}
            roughness={0.1}
          />
        </mesh>
      </group>

      {/* Right Leg */}
      <group ref={rightLegRef} position={[0.25, -0.5, 0]}>
        {/* Hip joint */}
        <mesh>
          <sphereGeometry args={[0.15, 32, 32]} />
          <meshStandardMaterial color="#b8b9d0" roughness={0.15} metalness={0.85} />
        </mesh>

        {/* Upper leg (thigh) */}
        <mesh position={[0, -0.35, 0]}>
          <capsuleGeometry args={[0.14, 0.5, 32, 32]} />
          <meshStandardMaterial color="#ffffff" roughness={0.1} metalness={0.95} />
        </mesh>
        {/* Thigh armor panel */}
        <mesh position={[0, -0.35, 0.15]}>
          <boxGeometry args={[0.22, 0.45, 0.08]} />
          <meshStandardMaterial color="#e8e9f3" roughness={0.12} metalness={0.92} />
        </mesh>
        {/* Thigh detail ring */}
        <mesh position={[0, -0.15, 0]}>
          <torusGeometry args={[0.145, 0.02, 16, 32]} />
          <meshStandardMaterial color="#60a5fa" roughness={0.15} metalness={0.9} />
        </mesh>

        {/* Knee joint */}
        <mesh position={[0, -0.65, 0]}>
          <sphereGeometry args={[0.16, 32, 32]} />
          <meshStandardMaterial color="#b8b9d0" roughness={0.15} metalness={0.85} />
        </mesh>
        {/* Knee cap detail */}
        <mesh position={[0, -0.65, 0.12]}>
          <capsuleGeometry args={[0.12, 0.08, 16, 32]} rotation={[0, 0, Math.PI / 2]} />
          <meshStandardMaterial color="#7c7d9a" roughness={0.2} metalness={0.8} />
        </mesh>

        {/* Lower leg (shin) */}
        <mesh position={[0, -0.95, 0]}>
          <capsuleGeometry args={[0.13, 0.45, 32, 32]} />
          <meshStandardMaterial color="#e8e9f3" roughness={0.12} metalness={0.92} />
        </mesh>
        {/* Shin armor panel */}
        <mesh position={[0, -0.95, 0.14]}>
          <boxGeometry args={[0.2, 0.4, 0.06]} />
          <meshStandardMaterial color="#ffffff" roughness={0.1} metalness={0.95} />
        </mesh>
        {/* Shin detail stripes */}
        <mesh position={[0, -0.85, 0.17]}>
          <boxGeometry args={[0.18, 0.03, 0.02]} />
          <meshStandardMaterial color="#60a5fa" roughness={0.15} metalness={0.9} />
        </mesh>
        <mesh position={[0, -1.05, 0.17]}>
          <boxGeometry args={[0.18, 0.03, 0.02]} />
          <meshStandardMaterial color="#60a5fa" roughness={0.15} metalness={0.9} />
        </mesh>

        {/* Ankle joint */}
        <mesh position={[0, -1.2, 0]}>
          <cylinderGeometry args={[0.11, 0.13, 0.1, 32]} />
          <meshStandardMaterial color="#b8b9d0" roughness={0.15} metalness={0.85} />
        </mesh>

        {/* Foot */}
        <mesh position={[0, -1.32, 0.08]}>
          <boxGeometry args={[0.22, 0.12, 0.35]} />
          <meshStandardMaterial color="#7c7d9a" roughness={0.18} metalness={0.88} />
        </mesh>
        {/* Foot top plate */}
        <mesh position={[0, -1.26, 0.08]}>
          <boxGeometry args={[0.24, 0.02, 0.37]} />
          <meshStandardMaterial color="#b8b9d0" roughness={0.15} metalness={0.9} />
        </mesh>
        {/* Toe section */}
        <mesh position={[0, -1.32, 0.22]}>
          <boxGeometry args={[0.22, 0.1, 0.12]} />
          <meshStandardMaterial color="#60a5fa" roughness={0.15} metalness={0.9} />
        </mesh>
        {/* Heel detail */}
        <mesh position={[0, -1.36, -0.06]}>
          <boxGeometry args={[0.2, 0.08, 0.1]} />
          <meshStandardMaterial color="#60a5fa" roughness={0.15} metalness={0.9} />
        </mesh>
        {/* Foot glow accent */}
        <mesh position={[0, -1.31, 0.25]}>
          <boxGeometry args={[0.15, 0.04, 0.02]} />
          <meshStandardMaterial
            color="#3b82f6"
            emissive="#3b82f6"
            emissiveIntensity={1.5}
            roughness={0.1}
          />
        </mesh>
      </group>
    </group>
  );
}

export const Avatar: React.FC<AvatarProps> = ({ isSpeaking }) => {
  const { avatarState } = useAvatarAnimation(isSpeaking);

  return (
    <div className="avatar-container">
      {/* Glow effect when speaking */}
      <AnimatePresence>
        {isSpeaking && (
          <motion.div
            className="avatar-glow-3d"
            initial={{ scale: 1, opacity: 0 }}
            animate={{
              scale: [1, 1.15, 1],
              opacity: [0.4, 0.6, 0.4],
            }}
            exit={{ scale: 1, opacity: 0 }}
            transition={{
              duration: 1.2,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        )}
      </AnimatePresence>

      {/* 3D Canvas for realistic avatar */}
      <div className="canvas-wrapper">
        <Canvas camera={{ position: [0, 0, 5], fov: 45 }} gl={{ alpha: true, antialias: true }}>
          <Suspense fallback={null}>
            {/* Lighting Setup */}
            <ambientLight intensity={0.7} />

            {/* Main directional light */}
            <directionalLight position={[3, 5, 4]} intensity={1.2} />

            {/* Fill lights for soft shadows */}
            <pointLight position={[-4, 2, 2]} intensity={0.6} color="#e0c3fc" />
            <pointLight position={[4, 2, 2]} intensity={0.6} color="#fde4e4" />
            <pointLight position={[0, -1, 3]} intensity={0.4} color="#ffffff" />

            {/* Rim light for depth */}
            <pointLight position={[0, 3, -3]} intensity={0.5} color="#60a5fa" />

            {/* Speaking glow light */}
            {isSpeaking && (
              <>
                <pointLight position={[0, 0, 3.5]} intensity={2} color="#3b82f6" />
                <pointLight position={[0, -0.5, 3]} intensity={1.5} color="#60a5fa" />
              </>
            )}

            {/* Cute Robot Avatar */}
            <CuteRobot isSpeaking={isSpeaking} />

            {/* Environment for realistic reflections */}
            <Environment preset="apartment" />

            {/* Orbit Controls - limited rotation */}
            <OrbitControls
              enableZoom={false}
              enablePan={false}
              minPolarAngle={Math.PI / 2.5}
              maxPolarAngle={Math.PI / 1.8}
              minAzimuthAngle={-Math.PI / 6}
              maxAzimuthAngle={Math.PI / 6}
              enableDamping
              dampingFactor={0.05}
            />
          </Suspense>
        </Canvas>
      </div>

      {/* Expression indicators */}
      <AnimatePresence>
        {avatarState.expression === 'thinking' && (
          <motion.div
            className="thinking-indicator-3d"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
          >
            <motion.span
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              •••
            </motion.span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Speaking indicator */}
      {isSpeaking && (
        <motion.div
          className="speaking-indicator-3d"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
        >
          <motion.div
            className="sound-wave"
            animate={{
              scaleY: [1, 1.5, 0.8, 1.3, 1],
            }}
            transition={{
              duration: 0.6,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
          <motion.div
            className="sound-wave"
            animate={{
              scaleY: [1, 0.8, 1.5, 1, 1.2],
            }}
            transition={{
              duration: 0.6,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 0.1,
            }}
          />
          <motion.div
            className="sound-wave"
            animate={{
              scaleY: [1, 1.3, 1, 1.5, 0.9],
            }}
            transition={{
              duration: 0.6,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 0.2,
            }}
          />
        </motion.div>
      )}
    </div>
  );
};
